import React, { useMemo, useState } from 'react';
import { Box, Button, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { formatBytes } from '../../utils/formatters';

const ROW_PAGE = 1000; // rows rendered per "page" in the table view
const RAW_HIGHLIGHT_LIMIT = 64 * 1024; // chars of raw text that get token spans; the rest is plain text
const DETECT_SAMPLE = 16 * 1024;
const CANDIDATES = [',', ';', '\t', '|'];
const DELIM_NAMES = { ',': 'Comma', ';': 'Semicolon', '\t': 'Tab', '|': 'Pipe' };

// ---------------------------------------------------------------------------
// Parsing (RFC 4180: quoted fields, "" escapes, delimiters/newlines inside quotes, CRLF)
// ---------------------------------------------------------------------------

/**
 * Parse CSV text into an array of rows (arrays of strings).
 * Returns { rows, openQuote, endsWithNewline } so callers can tell whether the
 * last record is complete.
 */
export const parseCsv = (input, delimiter = ',', maxChars = Infinity) => {
  const text = input.length > maxChars ? input.slice(0, maxChars) : input;
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  let i = 0;
  const n = text.length;
  let start = text.charCodeAt(0) === 0xfeff ? 1 : 0; // skip BOM
  i = start;
  while (i < n) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i += 2; continue; }
        inQuotes = false; i += 1; continue;
      }
      // Copy a run of ordinary characters in one go.
      const next = text.indexOf('"', i);
      const end = next === -1 ? n : next;
      field += text.slice(i, end);
      i = end;
      continue;
    }
    if (ch === '"' && field === '') { inQuotes = true; i += 1; continue; }
    if (ch === delimiter) { row.push(field); field = ''; i += 1; continue; }
    if (ch === '\r' || ch === '\n') {
      row.push(field); rows.push(row); row = []; field = '';
      i += ch === '\r' && text[i + 1] === '\n' ? 2 : 1;
      continue;
    }
    field += ch;
    i += 1;
  }
  const last = text[n - 1];
  const endsWithNewline = n > start && (last === '\n' || last === '\r') && !inQuotes;
  if (!endsWithNewline && (field !== '' || row.length > 0 || inQuotes)) {
    row.push(field);
    rows.push(row);
  }
  return { rows, openQuote: inQuotes, endsWithNewline };
};

/** Pick the delimiter among , ; tab | that splits the sample most consistently. */
export const detectDelimiter = (text) => {
  const sample = text.slice(0, DETECT_SAMPLE);
  let best = ',';
  let bestScore = -1;
  CANDIDATES.forEach((d) => {
    const { rows, endsWithNewline } = parseCsv(sample, d);
    const complete = endsWithNewline || rows.length < 2 ? rows : rows.slice(0, -1);
    const counts = complete.filter((r) => !(r.length === 1 && r[0] === '')).map((r) => r.length);
    if (!counts.length) return;
    const freq = {};
    counts.forEach((c) => { freq[c] = (freq[c] || 0) + 1; });
    let mode = 1; let modeFreq = 0;
    Object.entries(freq).forEach(([c, f]) => {
      if (f > modeFreq || (f === modeFreq && Number(c) > mode)) { mode = Number(c); modeFreq = f; }
    });
    if (mode < 2) return;
    // Consistency first, then more columns.
    const score = (modeFreq / counts.length) * 1000 + Math.min(mode, 999);
    if (score > bestScore) { bestScore = score; best = d; }
  });
  return best;
};

// ---------------------------------------------------------------------------
// Cell classification
// ---------------------------------------------------------------------------

const NULL_RE = /^(?:null|none|nil|n\/a|na|nan)$/i;
const NUMBER_RE = /^[+-]?(?:\d{1,3}(?:[,  ]\d{3})+|\d+)?(?:\.\d+)?(?:[eE][+-]?\d+)?%?$/;
const COMMA_DECIMAL_RE = /^[+-]?\d+(?:[  ]\d{3})*,\d+%?$/;
const BOOL_RE = /^(?:true|false)$/i;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2}(?:[.,]\d+)?)?(?:\s?(?:Z|[+-]\d{2}:?\d{2}))?)?$/;
const OTHER_DATE_RE = /^(?:\d{4}[/.]\d{1,2}[/.]\d{1,2}|\d{1,2}[/.-]\d{1,2}[/.-]\d{4})(?:[ T]\d{1,2}:\d{2}(?::\d{2})?(?:\s?[AaPp][Mm])?)?$/;
const TIME_RE = /^\d{1,2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/;

/** Returns 'empty' | 'number' | 'boolean' | 'date' | 'text'. */
export const classifyCell = (raw) => {
  if (raw == null) return 'empty';
  const v = String(raw).trim();
  if (v === '' || NULL_RE.test(v)) return 'empty';
  if (BOOL_RE.test(v)) return 'boolean';
  if (/\d/.test(v) && (NUMBER_RE.test(v) || COMMA_DECIMAL_RE.test(v))) return 'number';
  if (ISO_DATE_RE.test(v) || OTHER_DATE_RE.test(v) || TIME_RE.test(v)) return 'date';
  return 'text';
};

/** Dominant type of each column (>= 90% of non-empty cells), or 'text'. */
export const columnTypes = (rows, columnCount, sampleSize = 500) => {
  const types = [];
  const sample = rows.slice(0, sampleSize);
  for (let c = 0; c < columnCount; c += 1) {
    const counts = {};
    let filled = 0;
    sample.forEach((r) => {
      const t = classifyCell(r[c]);
      if (t === 'empty') return;
      filled += 1;
      counts[t] = (counts[t] || 0) + 1;
    });
    const [top, topCount] = Object.entries(counts).sort((a, b) => b[1] - a[1])[0] || ['text', 0];
    types.push(filled && topCount / filled >= 0.9 ? top : 'text');
  }
  return types;
};

// ---------------------------------------------------------------------------
// Styles: colour roles shared with JsonViewer (theme palette, light/dark aware)
// ---------------------------------------------------------------------------

const pick = (name) => (theme) => {
  const c = theme.palette[name];
  return theme.palette.mode === 'dark' ? c.light : c.dark;
};

const colourSx = {
  '& .csv-number': { color: pick('warning') },
  '& .csv-boolean': { color: pick('secondary') },
  '& .csv-date': { color: pick('primary') },
  '& .csv-empty': { color: 'text.disabled' },
  '& .csv-string': { color: pick('success') },
  '& .csv-delim': { color: 'text.disabled' },
};

const surfaceSx = {
  border: 1,
  borderColor: 'divider',
  borderRadius: 1,
  overflow: 'auto',
  bgcolor: 'background.paper',
  colorScheme: (theme) => theme.palette.mode, // native scrollbars follow the theme
};

const tableSx = (theme) => {
  const zebra = theme.palette.action.hover;
  const paper = theme.palette.background.paper;
  // Sticky cells need an opaque background; layer the zebra tint over paper.
  const tint = (c) => `linear-gradient(${c}, ${c}), linear-gradient(${paper}, ${paper})`;
  const headBg = theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[100];
  return {
    borderCollapse: 'separate',
    borderSpacing: 0,
    fontSize: '0.8125rem',
    lineHeight: 1.5,
    minWidth: '100%',
    '& th, & td': {
      px: 1.25,
      py: 0.5,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: 320,
      borderBottom: `1px solid ${theme.palette.divider}`,
      textAlign: 'left',
      verticalAlign: 'top',
    },
    '& td + td, & th + th': { borderLeft: `1px solid ${theme.palette.divider}` },
    '& thead th': {
      position: 'sticky',
      top: 0,
      zIndex: 2,
      fontWeight: 600,
      background: headBg,
      color: 'text.primary',
      borderBottom: `2px solid ${theme.palette.divider}`,
    },
    '& tbody tr:nth-of-type(even) td': { background: tint(zebra) },
    '& tbody tr:hover td': { background: tint(theme.palette.action.selected) },
    '& .csv-rownum': {
      position: 'sticky',
      left: 0,
      zIndex: 1,
      background: paper,
      color: 'text.secondary',
      textAlign: 'right',
      fontVariantNumeric: 'tabular-nums',
      userSelect: 'none',
      minWidth: 40,
    },
    '& thead .csv-rownum': { zIndex: 3, background: headBg },
    '& .csv-right': { textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
    '& .csv-number': { fontVariantNumeric: 'tabular-nums' },
    ...colourSx,
  };
};

const rawSx = {
  ...surfaceSx,
  ...colourSx,
  m: 0,
  p: 1.5,
  fontFamily: 'monospace',
  fontSize: '0.8125rem',
  lineHeight: 1.6,
  whiteSpace: 'pre',
  '& .csv-header': { fontWeight: 700 },
};

// ---------------------------------------------------------------------------
// Raw view highlighting
// ---------------------------------------------------------------------------

/** Tokenise raw CSV text into spans: delimiters muted, quoted strings coloured, header bold. */
export const highlightRaw = (text, delimiter) => {
  const out = [];
  let plain = '';
  let headerOpen = true;
  let header = [];
  const flush = () => {
    if (!plain) return;
    (headerOpen ? header : out).push(plain);
    plain = '';
  };
  const push = (node) => { flush(); (headerOpen ? header : out).push(node); };
  let i = 0;
  let k = 0;
  const n = text.length;
  while (i < n) {
    const ch = text[i];
    if (ch === '"') {
      // Find the closing quote, skipping "" escapes.
      let j = i + 1;
      while (j < n) {
        if (text[j] === '"') { if (text[j + 1] === '"') { j += 2; continue; } break; }
        j += 1;
      }
      const end = Math.min(j + 1, n);
      push(<span key={k += 1} className="csv-string">{text.slice(i, end)}</span>);
      i = end;
    } else if (ch === delimiter) {
      push(<span key={k += 1} className="csv-delim">{ch}</span>);
      i += 1;
    } else if (headerOpen && (ch === '\n' || ch === '\r')) {
      flush();
      out.push(<span key="header" className="csv-header">{header}</span>);
      headerOpen = false;
      header = null;
      plain += ch;
      i += 1;
    } else {
      plain += ch;
      i += 1;
    }
  }
  flush();
  if (headerOpen && header.length) out.push(<span key="header" className="csv-header">{header}</span>);
  return out;
};

const RawView = ({ text, delimiter, maxHeight }) => {
  const head = text.length > RAW_HIGHLIGHT_LIMIT ? text.slice(0, RAW_HIGHLIGHT_LIMIT) : text;
  const tail = text.length > RAW_HIGHLIGHT_LIMIT ? text.slice(RAW_HIGHLIGHT_LIMIT) : '';
  const body = useMemo(() => highlightRaw(head, delimiter), [head, delimiter]);
  return (
    <Box component="pre" sx={{ ...rawSx, maxHeight }} data-testid="csv-raw">
      {body}
      {tail}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Table view
// ---------------------------------------------------------------------------

const Cell = ({ value, align }) => {
  const type = classifyCell(value);
  if (type === 'empty') {
    return <td className={align} title={value ? value : 'Empty'}><span className="csv-empty">{value ? value : '—'}</span></td>;
  }
  const cls = [align, type === 'text' ? '' : `csv-${type}`].filter(Boolean).join(' ');
  return <td className={cls || undefined} title={value.length > 24 ? value : undefined}>{value}</td>;
};

const TableView = ({ header, body, types, maxHeight }) => {
  const [limit, setLimit] = useState(ROW_PAGE);
  const shown = body.length > limit ? body.slice(0, limit) : body;
  const remaining = body.length - shown.length;
  return (
    <>
      <Box sx={{ ...surfaceSx, maxHeight }}>
        <Box component="table" sx={tableSx}>
          <thead>
            <tr>
              <th className="csv-rownum" scope="col" aria-label="Row number">#</th>
              {header.map((h, c) => (
                <th key={c} scope="col" className={types[c] === 'number' ? 'csv-right' : undefined}
                  title={`${h || `Column ${c + 1}`} (${types[c]})`}>
                  {h || <span className="csv-empty">{`Column ${c + 1}`}</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((r, i) => (
              <tr key={i}>
                <th scope="row" className="csv-rownum" style={{ fontWeight: 400 }}>{i + 1}</th>
                {header.map((_, c) => (
                  <Cell key={c} value={r[c] ?? ''} align={types[c] === 'number' ? 'csv-right' : undefined} />
                ))}
              </tr>
            ))}
          </tbody>
        </Box>
      </Box>
      {remaining > 0 && (
        <Box display="flex" alignItems="center" gap={1} mt={1}>
          <Typography variant="caption" color="text.secondary">
            {`Rendering ${shown.length.toLocaleString()} of ${body.length.toLocaleString()} rows.`}
          </Typography>
          <Button size="small" onClick={() => setLimit((l) => l + ROW_PAGE)}>
            {`Show ${Math.min(ROW_PAGE, remaining).toLocaleString()} more`}
          </Button>
        </Box>
      )}
    </>
  );
};

// ---------------------------------------------------------------------------

/**
 * Formatted, type-highlighted CSV/TSV preview with a table and a raw view.
 *
 * Props: text (string), truncated (bool, preview is a prefix of the file),
 * totalSize (bytes of the whole file), delimiter (optional override),
 * maxHeight (CSS length for the scroll area).
 */
const CsvViewer = ({ text, truncated = false, totalSize, delimiter: forced, maxHeight = '60vh' }) => {
  const [view, setView] = useState('table');
  const content = text || '';
  const delimiter = useMemo(() => forced || detectDelimiter(content), [forced, content]);

  const parsed = useMemo(() => {
    const { rows, openQuote, endsWithNewline } = parseCsv(content, delimiter);
    let all = rows;
    let dropped = false;
    // A truncated preview almost always cuts the last record mid-way.
    if (truncated && all.length > 1 && (openQuote || !endsWithNewline)) {
      all = all.slice(0, -1);
      dropped = true;
    }
    all = all.filter((r) => !(r.length === 1 && r[0] === '')); // blank lines
    const [header = [], ...body] = all;
    const width = all.reduce((m, r) => Math.max(m, r.length), 0);
    const fullHeader = header.concat(Array(Math.max(0, width - header.length)).fill(''));
    return { header: fullHeader, body, dropped };
  }, [content, delimiter, truncated]);

  const types = useMemo(() => columnTypes(parsed.body, parsed.header.length), [parsed]);

  const bytesShown = useMemo(() => new Blob([content]).size, [content]);
  const rowsLabel = `${parsed.body.length.toLocaleString()} row${parsed.body.length === 1 ? '' : 's'}`;
  const colsLabel = `${parsed.header.length} column${parsed.header.length === 1 ? '' : 's'}`;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
          {truncated
            ? `Showing first ${rowsLabel} (${formatBytes(bytesShown)} of ${formatBytes(totalSize)}). Download the file to see all rows.`
            : `${rowsLabel}, ${colsLabel}`}
          <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 1 }}>
            {`${DELIM_NAMES[delimiter] || 'Custom'}-separated`}
          </Typography>
        </Typography>
        <ToggleButtonGroup size="small" exclusive value={view}
          onChange={(_, v) => v && setView(v)} aria-label="CSV view">
          <ToggleButton value="table" aria-label="Table view">Table</ToggleButton>
          <ToggleButton value="raw" aria-label="Raw text view">Raw</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {view === 'raw' ? (
        <RawView text={content} delimiter={delimiter} maxHeight={maxHeight} />
      ) : parsed.header.length === 0 ? (
        <Typography variant="body2" color="text.secondary">This file is empty.</Typography>
      ) : (
        <TableView header={parsed.header} body={parsed.body} types={types} maxHeight={maxHeight} />
      )}
    </Box>
  );
};

export default CsvViewer;
